import { forwardRef } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { ChannelTypeBadge } from "./ChannelTypeBadge";
import type {
  ChannelType,
  ConversationStatus,
} from "@/modules/chatbot/types";

/**
 * InboxFilterBar — channel + state + assignee + qidiruv.
 */

export interface InboxFilterValue {
  channelType?: ChannelType;
  status?: ConversationStatus;
  assignedUserId?: "me" | "unassigned" | string;
  q?: string;
}

export interface InboxFilterBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: InboxFilterValue;
  onChange: (next: InboxFilterValue) => void;
  channels?: ChannelType[];
}

const STATUS_OPTIONS: Array<{ value: ConversationStatus | ""; label: string }> = [
  { value: "", label: "Barcha holatlar" },
  { value: "OPEN", label: "Ochiq" },
  { value: "AI_HANDLING", label: "AI" },
  { value: "HUMAN_HANDOFF", label: "Operator" },
  { value: "CLOSED", label: "Yopilgan" },
  { value: "SPAM", label: "Spam" },
];

const ASSIGNEE_OPTIONS = [
  { value: "", label: "Hammasi" },
  { value: "me", label: "Menga" },
  { value: "unassigned", label: "Tayinlanmagan" },
];

const ALL_CHANNELS: ChannelType[] = [
  "INSTAGRAM_DM",
  "TELEGRAM",
  "MESSENGER",
  "WHATSAPP",
];

export const InboxFilterBar = forwardRef<HTMLDivElement, InboxFilterBarProps>(
  ({ value, onChange, channels = ALL_CHANNELS, className, ...props }, ref) => {
    const hasFilters = Boolean(
      value.channelType || value.status || value.assignedUserId || value.q,
    );

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-2 border-b border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2.5",
          className,
        )}
        {...props}
      >
        {/* Search */}
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]"
            aria-hidden
          />
          <input
            type="search"
            value={value.q ?? ""}
            onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
            placeholder="Suhbat qidirish..."
            className="w-full rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] py-1.5 pl-8 pr-8 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--accent-violet)] focus:outline-none"
          />
          {value.q && (
            <button
              type="button"
              onClick={() => onChange({ ...value, q: undefined })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              aria-label="Qidiruvni tozalash"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>

        {/* Channel pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterChip
            active={!value.channelType}
            onClick={() => onChange({ ...value, channelType: undefined })}
          >
            Barcha kanallar
          </FilterChip>
          {channels.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ ...value, channelType: c })}
              className={cn(
                "rounded-full transition-opacity",
                value.channelType === c ? "opacity-100" : "opacity-60 hover:opacity-100",
              )}
            >
              <ChannelTypeBadge type={c} size="sm" />
            </button>
          ))}
        </div>

        {/* Selects */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={value.status ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                status: (e.target.value || undefined) as ConversationStatus | undefined,
              })
            }
            className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1 text-xs text-[var(--text-primary)] focus:border-[var(--accent-violet)] focus:outline-none"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={value.assignedUserId ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                assignedUserId: (e.target.value || undefined) as InboxFilterValue["assignedUserId"],
              })
            }
            className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1 text-xs text-[var(--text-primary)] focus:border-[var(--accent-violet)] focus:outline-none"
          >
            {ASSIGNEE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              Tozalash
            </button>
          )}
        </div>
      </div>
    );
  },
);
InboxFilterBar.displayName = "InboxFilterBar";

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
        active
          ? "border-[var(--accent-violet)] bg-[var(--accent-violet)]/15 text-[var(--text-primary)]"
          : "border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
      )}
    >
      {children}
    </button>
  );
}
